import { loadMyApplications, submitLeaseApplication } from 'api/services/applicationService'
import useAuthService from 'api/services/authService'
import { loadMyVerification } from 'api/services/verificationService'
import { setPendingApplyListingId } from 'api/utils/pendingApplyListing'
import NativeDateInput from 'components/common/field/NativeDateInput'
import SecurityVerificationModal from 'components/verification/SecurityVerificationModal'
import { submitTenantVerificationDocuments } from 'components/verification/submitTenantVerification'
import { canProceedAfterTenantVerification } from 'components/verification/verificationUtils'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const CreateProfilePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useLocation()
  const state = location.state as { source?: string; propertyId?: string } | null
  const source = state?.source
  const propertyId = state?.propertyId
  const { isAuthenticated } = useAuthService()

  const [moveInDate, setMoveInDate] = useState('')
  const [incomeRange, setIncomeRange] = useState('')
  const [occupants, setOccupants] = useState('')
  const [hasPets, setHasPets] = useState<'yes' | 'no' | null>(null)
  const [acceptCheck, setAcceptCheck] = useState(false)
  const [message, setMessage] = useState('')
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [isSubmittingVerification, setIsSubmittingVerification] = useState(false)
  const hasHydratedDefaults = useRef(false)

  const canSubmit =
    !!moveInDate && !!incomeRange && !!occupants && hasPets !== null && acceptCheck

  const primaryLabel = source === 'register' ? 'Criar perfil' : 'Envoyer ma candidature'

  const { mutateAsync, isLoading } = useMutation(submitLeaseApplication)
  const { data: previousApplications } = useQuery(
    ['myApplicationsForPrefill'],
    loadMyApplications,
    { enabled: isAuthenticated }
  )

  const { data: identityVerification } = useQuery(
    ['myVerification'],
    loadMyVerification,
    { enabled: isAuthenticated }
  )

  useEffect(() => {
    if (hasHydratedDefaults.current) return
    if (!isAuthenticated) return
    if (!Array.isArray(previousApplications) || previousApplications.length === 0) return

    const latest = previousApplications[0]
    if (!latest) return

    if (latest.move_in_date) setMoveInDate(String(latest.move_in_date))
    if (latest.income_range) setIncomeRange(String(latest.income_range))
    if (latest.occupants) setOccupants(String(latest.occupants))
    if (latest.has_pets === 'yes' || latest.has_pets === 'no') setHasPets(latest.has_pets)
    setAcceptCheck(Boolean(latest.accept_credit_check))
    if (latest.message) setMessage(String(latest.message))

    hasHydratedDefaults.current = true
  }, [isAuthenticated, previousApplications])

  const submitApplication = async () => {
    await mutateAsync({
      propertyId: propertyId ?? null,
      source: propertyId ? 'listing' : source === 'register' ? 'register' : 'listing',
      moveInDate,
      incomeRange,
      occupants: Number(occupants),
      hasPets: hasPets!,
      acceptCreditCheck: acceptCheck,
      message: message.trim() || undefined,
    })
    toast.success('Candidature envoyée.')
    if (propertyId) {
      navigate(`/listing/${propertyId}`, { replace: true })
    } else {
      navigate('/listings', { replace: true })
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    if (!isAuthenticated) {
      if (propertyId) {
        setPendingApplyListingId(propertyId)
        navigate('/auth', {
          state: {
            applyListingId: propertyId,
            returnTo: `/listing/${propertyId}`,
          },
        })
      } else {
        navigate('/auth')
      }
      toast.info('Connecte-toi pour envoyer ta candidature.')
      return
    }

    if (!canProceedAfterTenantVerification(identityVerification)) {
      setShowVerificationModal(true)
      return
    }

    try {
      await submitApplication()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Une erreur est survenue.'
      toast.error(msg)
    }
  }

  const handleVerificationSubmit = async (
    files: Parameters<typeof submitTenantVerificationDocuments>[0]
  ) => {
    setIsSubmittingVerification(true)
    try {
      await submitTenantVerificationDocuments(files)
      await queryClient.invalidateQueries(['myVerification'])
      const updated = await loadMyVerification()
      setShowVerificationModal(false)

      if (canProceedAfterTenantVerification(updated)) {
        await submitApplication()
        return
      }

      toast.info(String(t('verification.pendingApprovalMessage')))
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('verification.submitFailed')
      toast.error(String(msg))
    } finally {
      setIsSubmittingVerification(false)
    }
  }

  return (
    <div className="min-h-screen bg-imovWhiteShadow flex items-center justify-center px-4">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-imovGray/30 p-6 sm:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-imovSecondary mb-1">
            Candidature
          </h1>
          <p className="text-sm text-gray-600">
            Remplis ce formulaire pour te pré‑qualifier et faciliter la reprise de bail.
            {propertyId ? (
              <span className="block mt-1 text-imovPrimary font-medium">
                Annonce n°{propertyId}
              </span>
            ) : null}
          </p>
        </div>

        <div className="flex flex-col gap-4 text-sm">
          <div>
            <label className="block text-xs font-semibold text-imovSecondary mb-1 uppercase tracking-wide">
              Date d&apos;emménagement souhaitée
            </label>
            <NativeDateInput
              value={moveInDate}
              onChange={e => setMoveInDate(e.target.value)}
              className="date-input-native w-full cursor-pointer rounded-xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:border-imovPrimary focus:outline-none focus:ring-2 focus:ring-imovPrimary/50"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-imovSecondary mb-1 uppercase tracking-wide">
              Fourchette de revenus annuels
            </label>
            <select
              value={incomeRange}
              onChange={e => setIncomeRange(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary focus:outline-none focus:ring-2 focus:ring-imovPrimary/50 focus:border-imovPrimary"
            >
              <option value="">Sélectionner une option</option>
              <option value="0-30000">0 - 30 000 $</option>
              <option value="30000-60000">30 000 - 60 000 $</option>
              <option value="60000-90000">60 000 - 90 000 $</option>
              <option value="90000+">90 000 $ et plus</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-imovSecondary mb-1 uppercase tracking-wide">
                Nombre d&apos;occupants
              </label>
              <input
                type="number"
                min={1}
                value={occupants}
                onChange={e => setOccupants(e.target.value)}
                placeholder="Ex: 2"
                className="w-full rounded-xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-imovPrimary/50 focus:border-imovPrimary"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-imovSecondary mb-1 uppercase tracking-wide">
                Animaux de compagnie
              </label>
              <div className="inline-flex rounded-full bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setHasPets('yes')}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full min-w-[4rem] ${
                    hasPets === 'yes'
                      ? 'bg-imovPrimary text-white shadow-sm'
                      : 'text-imovSecondary'
                  }`}
                >
                  Oui
                </button>
                <button
                  type="button"
                  onClick={() => setHasPets('no')}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full min-w-[4rem] ${
                    hasPets === 'no'
                      ? 'bg-imovPrimary text-white shadow-sm'
                      : 'text-imovSecondary'
                  }`}
                >
                  Non
                </button>
              </div>
            </div>
          </div>

          <label className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-imovWhiteShadow px-3 py-3 text-xs text-imovSecondary cursor-pointer">
            <input
              type="checkbox"
              checked={acceptCheck}
              onChange={e => setAcceptCheck(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary/50"
            />
            <span>
              J&apos;accepte que le propriétaire actuel ou futur effectue une enquête de crédit
              et de références si nécessaire.
            </span>
          </label>

          <div>
            <label className="block text-xs font-semibold text-imovSecondary mb-1 uppercase tracking-wide">
              Message optionnel
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Parle‑nous un peu de toi..."
              rows={4}
              className="w-full rounded-2xl border border-gray-300 bg-imovWhite px-3 py-2.5 text-sm text-imovSecondary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-imovPrimary/50 focus:border-imovPrimary resize-none"
            />
          </div>

          <button
            type="button"
            disabled={!canSubmit || isLoading || isSubmittingVerification}
            onClick={() => void handleSubmit()}
            className={`mt-2 w-full rounded-full py-3 text-sm font-semibold text-white flex items-center justify-center gap-1 ${
              canSubmit && !isLoading && !isSubmittingVerification
                ? 'bg-[#0056D2] hover:bg-[#0046b3] cursor-pointer'
                : 'bg-[#9BBDF5] cursor-not-allowed'
            }`}
          >
            {isLoading || isSubmittingVerification ? 'Envoi…' : primaryLabel}
            {!isLoading && !isSubmittingVerification ? (
              <span className="text-base ml-1">{'>'}</span>
            ) : null}
          </button>

          <div className="mt-3 flex items-center justify-center gap-6 text-[0.65rem] text-gray-400 uppercase tracking-wide">
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-imovPrimary" />
              <span>Sécurisé</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-imovPrimary" />
              <span>Confidentialité</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-imovPrimary" />
              <span>Québec</span>
            </div>
          </div>
        </div>
      </div>

      <SecurityVerificationModal
        isOpen={showVerificationModal}
        mode="tenant"
        isLoading={isSubmittingVerification}
        onClose={() => setShowVerificationModal(false)}
        onSubmit={files => void handleVerificationSubmit(files)}
      />
    </div>
  )
}

export default CreateProfilePage
